import Order from "../components/Order";
import { useState, useEffect } from "react";
import PendingOrders from "../components/PendingOrders";
import PreviousOrders from "../components/PreviousOrders";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import { useNavigate } from "react-router-dom";
import { orderService } from "../services";
import { OrderI } from "../utils/types";

const AllOrder = () => {
  const [activeTab, setActiveTab] = useState<"pending" | "previous">("pending");
  const [selectedOrderIndex, setSelectedOrderIndex] = useState<number>(-1);
  const [showMobileDetail, setShowMobileDetail] = useState<boolean>(false);
  const [orders, setOrders] = useState<OrderI[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage] = useState<number>(1);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<OrderI | null>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response: any = await orderService.getOrders({
          page: currentPage,
          limit: 10,
        });

        if (response && response.data) {
          setOrders(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage]);

  const pendingStatuses = [
    "placed",
    "pending",
    "confirmed",
    "preparing",
    "ready_for_pickup",
    "out_for_delivery",
  ];
  const completedStatuses = ["delivered", "cancelled", "completed"];

  const pendingOrders = orders.filter((order) =>
    pendingStatuses.includes(order.orderStatus)
  );

  const previousOrders = orders.filter((order) =>
    completedStatuses.includes(order.orderStatus)
  );

  const displayOrders = activeTab === "pending" ? pendingOrders : previousOrders;

  const handleOrderClick = async (index: number, orderId: string) => {
    setSelectedOrderIndex(index);
    setShowMobileDetail(true);

    try {
      setLoadingOrderDetails(true);
      const response: any = await orderService.getOrderById(orderId);

      if (response && response.data) {
        setSelectedOrderDetails(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  return (
    <div className="flex border-0 md:border-2 dark:border-gray-700 bg-[#FFFFFF] dark:bg-gray-900 overflow-x-hidden transition-colors duration-300 min-h-screen">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 z-50 px-5 py-4 flex items-center justify-between shadow-sm transition-colors duration-300">
        <button
          onClick={() =>
            showMobileDetail ? setShowMobileDetail(false) : navigate(-1)
          }
          className="w-10 h-10 flex items-center justify-center"
        >
          <img src={arrowLeft} alt="back" className="w-6 h-6 dark:invert" />
        </button>
        <h1 className="text-gray-900 dark:text-white font-urbanist text-lg font-semibold">
          {showMobileDetail && activeTab === "pending"
            ? "Your Order"
            : "Your Orders"}
        </h1>
        <div className="w-10 h-10" />
      </div>

      {/* Left Section - Order List */}
      <div
        className={`w-full md:w-[35%] h-full min-h-[100vh] border-r dark:border-gray-700 p-0 md:p-6 my-16 md:my-0 ${
          showMobileDetail ? "hidden md:block" : "block"
        }`}
      >
        <div className="hidden md:block mb-4">
          <h1 className="text-[#050505] dark:text-white font-urbanist text-2xl font-semibold">
            Your Orders
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-4 md:px-0">
          <button
            onClick={() => {
              setActiveTab("pending");
              setShowMobileDetail(false);
              setSelectedOrderIndex(-1);
              setSelectedOrderDetails(null);
            }}
            className={`relative flex-1 md:flex-none px-4 md:px-6 py-3 text-sm font-medium transition-colors duration-200
            ${
              activeTab === "pending"
                ? "text-[#7402A7]"
                : "text-[#050505] dark:text-gray-300"
            }`}
          >
            Pending Orders
            {activeTab === "pending" && (
              <span className="absolute left-0 bottom-0 h-[3px] w-full bg-[#7402A7] rounded-full" />
            )}
          </button>

          <button
            onClick={() => {
              setActiveTab("previous");
              setShowMobileDetail(false);
              setSelectedOrderIndex(-1);
              setSelectedOrderDetails(null);
            }}
            className={`relative flex-1 md:flex-none px-4 md:px-6 py-3 text-sm font-medium transition-colors duration-200
            ${
              activeTab === "previous"
                ? "text-[#7402A7]"
                : "text-[#050505] dark:text-gray-300"
            }`}
          >
            Previous Orders
            {activeTab === "previous" && (
              <span className="absolute left-0 bottom-0 h-[3px] w-full bg-[#7402A7] rounded-full" />
            )}
          </button>
        </div>

        <div className="px-4 md:px-0 pb-24 md:pb-0">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <p className="text-gray-500">Loading orders...</p>
            </div>
          ) : displayOrders.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <p className="text-gray-500">No orders found</p>
            </div>
          ) : (
            displayOrders.map((order, index) => (
              <div
                key={order._id}
                onClick={() => handleOrderClick(index, order._id)}
                className="cursor-pointer transition-colors"
              >
                <Order
                  order={order}
                  selectedOrderIndex={selectedOrderIndex}
                  index={index}
                  tab={activeTab}
                  onRated={(orderId, ratingValue) => {
                    setOrders((prev) =>
                      prev.map((o) =>
                        o._id === orderId
                          ? {
                              ...o,
                              rating: [
                                {
                                  _id: `local-${orderId}`,
                                  ratingType: "restaurant",
                                  orderId,
                                  restaurantId: o.restaurantId,
                                  rating: ratingValue,
                                },
                              ],
                            }
                          : o
                      )
                    );
                    setSelectedOrderDetails((prev) =>
                      prev && prev._id === orderId
                        ? {
                            ...prev,
                            rating: [
                              {
                                _id: `local-${orderId}`,
                                ratingType: "restaurant",
                                orderId,
                                restaurantId: prev.restaurantId,
                                rating: ratingValue,
                              },
                            ],
                          }
                        : prev
                    );
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Section - Order Details */}
      <div
        className={`w-full md:w-[65%] border-l dark:border-gray-700 ${
          showMobileDetail ? "block" : "hidden md:block"
        }`}
      >
        {loadingOrderDetails ? (
          <div className="flex items-center justify-center h-full py-20">
            <p className="text-gray-500">Loading order details...</p>
          </div>
        ) : !selectedOrderDetails ? (
          <div className="hidden md:flex items-center justify-center h-full py-20">
            <p className="text-gray-500">Select an order to view details</p>
          </div>
        ) : activeTab === "pending" ? (
          <PendingOrders
            orders={[selectedOrderDetails]}
            onBack={() => {
              setShowMobileDetail(false);
              setSelectedOrderIndex(-1);
              setSelectedOrderDetails(null);
            }}
          />
        ) : (
          <PreviousOrders
            orders={[selectedOrderDetails]}
            onRated={(orderId, ratingValue) => {
                setOrders((prev) =>
                  prev.map((o) =>
                    o._id === orderId
                      ? {
                          ...o,
                          rating: [
                            {
                              _id: `local-${orderId}`,
                              ratingType: "restaurant",
                              orderId,
                              restaurantId: o.restaurantId,
                              rating: ratingValue,
                            },
                          ],
                        }
                      : o
                  )
                );
                setSelectedOrderDetails((prev) =>
                  prev && prev._id === orderId
                    ? {
                        ...prev,
                        rating: [
                          {
                            _id: `local-${orderId}`,
                            ratingType: "restaurant",
                            orderId,
                            restaurantId: prev.restaurantId,
                            rating: ratingValue,
                          },
                        ],
                      }
                    : prev
                );
              }}
            />
        )}
      </div>
    </div>
  );
};

export default AllOrder;
